/**
 * StrategyResult Component
 * 
 * Displays comprehensive race strategy information including:
 * - Summary statistics (laps, stints, pit stops)
 * - Total race time
 * - Fuel save analysis
 * - Detailed stint breakdown
 */

import React, { useState, useRef, useEffect } from "react";
import { Strategy, PitStopDetails } from "../types/strategy";

/**
 * Props interface for the StrategyResult component
 */
interface Props {
  strategy: Strategy;  // Strategy data to display
  title: string;       // Title for the strategy section
  userActualLapTime?: number | null; // User's actual achievable lap time
  onUserActualLapTimeChange?: (value: number | null) => void; // Callback for actual lap time changes
  userActualFuelConsumption?: number | null; // User's actual fuel consumption while fuel saving
  onUserActualFuelConsumptionChange?: (value: number | null) => void; // Callback for fuel consumption changes
  fuelCapacity: number; // Fuel tank capacity in liters
  pitStopDetails: PitStopDetails; // Pit stop timing details from user input
  simpleFuelSaveInfo?: { // Simple fuel save strategy info (+1 lap per stint)
    requiredFuelSave: number;
    maxTimeLossPerLap: number;
    adjustedFuelConsumption: number;
    adjustedLapTime: number;
    lapAdvantage: number;
    timeAdvantageAtFinish: number;
  } | null;
}

/**
 * StrategyResult component for displaying race strategy information
 * @param props - Component props
 * @returns JSX element or null if no strategy provided
 */
export const StrategyResult: React.FC<Props> = ({ strategy, title, userActualLapTime, onUserActualLapTimeChange, userActualFuelConsumption, onUserActualFuelConsumptionChange, fuelCapacity, pitStopDetails, simpleFuelSaveInfo }) => {
  if (!strategy) return null;

  // Local state for the actual lap time input field
  const [actualLapTimeInputValue, setActualLapTimeInputValue] = useState("");
  const [actualFuelConsumptionInputValue, setActualFuelConsumptionInputValue] = useState("");
  const lastActualLapTimeRef = useRef(userActualLapTime);
  const lastActualFuelConsumptionRef = useRef(userActualFuelConsumption);
  
  // State for section collapse
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // State for expanded stints
  const [expandedStints, setExpandedStints] = useState<Set<number>>(new Set());

  // Update actual lap time input value when userActualLapTime changes externally
  useEffect(() => {
    if (lastActualLapTimeRef.current !== userActualLapTime) {
      setActualLapTimeInputValue(userActualLapTime ? formatLapTime(userActualLapTime) : "");
      lastActualLapTimeRef.current = userActualLapTime;
    }
  }, [userActualLapTime]);

  // Update actual fuel consumption input value when userActualFuelConsumption changes externally
  useEffect(() => {
    if (lastActualFuelConsumptionRef.current !== userActualFuelConsumption) {
      setActualFuelConsumptionInputValue(userActualFuelConsumption ? userActualFuelConsumption.toFixed(2) : "");
      lastActualFuelConsumptionRef.current = userActualFuelConsumption;
    }
  }, [userActualFuelConsumption]);

  // Initialize on mount
  useEffect(() => {
    if (userActualLapTime) {
      setActualLapTimeInputValue(formatLapTime(userActualLapTime));
      lastActualLapTimeRef.current = userActualLapTime;
    }
    if (userActualFuelConsumption) {
      setActualFuelConsumptionInputValue(userActualFuelConsumption.toFixed(2));
      lastActualFuelConsumptionRef.current = userActualFuelConsumption;
    }
  }, []);

  /**
   * Format time in seconds to human-readable format
   * @param seconds - Time in seconds
   * @returns Formatted time string (e.g., "2h 15m 30s")
   */
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  /**
   * Format lap time in seconds to MM:SS.MSMSMS format
   * @param seconds - Lap time in seconds
   * @returns Formatted lap time string (e.g., "01:47.500")
   */
  const formatLapTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 1000);
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
  };

  /**
   * Toggle stint expansion
   * @param stintNumber - The stint number to toggle
   */
  const toggleStintExpansion = (stintNumber: number) => {
    const newExpanded = new Set(expandedStints);
    if (newExpanded.has(stintNumber)) {
      newExpanded.delete(stintNumber);
    } else {
      newExpanded.add(stintNumber);
    }
    setExpandedStints(newExpanded);
  };

  /**
   * Calculate detailed stint information
   * @param stint - The stint to calculate details for
   * @param previousStintEndTime - Time when previous stint ended
   * @param nextStint - The next stint (for refuel calculation)
   * @returns Detailed stint information
   */
  const calculateStintDetails = (stint: any, previousStintEndTime: number = 0, nextStint?: any) => {
    const stintStartTime = previousStintEndTime;
    const stintEndTime = stintStartTime + stint.time;
    // Pit stop time is included in the stint where we pit in (last lap of the stint)
    const pitStopEndTime = stintEndTime + (stint.pitStopAfter ? stint.pitStopTime : 0);
    
    // Calculate refuel amount - we refuel the amount we need for the next stint
    // For the last stint, we don't refuel (no next stint)
    // Also respect the fuel capacity limit
    let refuelAmount = 0;
    if (stint.pitStopAfter && nextStint) {
      const currentFuelLevel = fuelCapacity - stint.fuelUsed;
      const fuelNeeded = nextStint.fuelUsed;
      const fuelToAdd = Math.min(fuelNeeded, fuelCapacity - currentFuelLevel);
      refuelAmount = fuelToAdd;
    }
    
    return {
      startTime: stintStartTime,
      endTime: stintEndTime,
      pitStopEndTime,
      avgLapTime: stint.time / stint.laps,
      fuelPerLap: stint.fuelUsed / stint.laps, // Always use actual fuel consumption per lap from the stint
      actualFuelPerLap: stint.fuelUsed / stint.laps, // Always use actual fuel consumption for calculations
      // Include pit stop time in the stint duration for timing calculations
      totalStintTime: stint.time + (stint.pitStopAfter ? stint.pitStopTime : 0),
      refuelAmount: refuelAmount
    };
  };

  return (
    <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 mb-6">
      {/* Strategy title - clickable to collapse/expand */}
      <div 
        className="flex items-center justify-between cursor-pointer select-none"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <h2 className="text-xl font-semibold text-white">{title}</h2>
        <span className={`text-slate-400 transition-transform duration-200 text-sm ${isCollapsed ? '' : 'rotate-180'}`}>▼</span>
      </div>
      
      {!isCollapsed && <>
      {/* Summary statistics grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 mt-4">
        <div className="text-center h-20 flex flex-col justify-center">
          <div className="text-2xl font-bold text-blue-400">{strategy.totalLaps}</div>
          <div className="text-sm text-slate-400">Total Laps</div>
        </div>
        <div className="text-center h-20 flex flex-col justify-center">
          <div className="text-2xl font-bold text-green-400">{strategy.stints}</div>
          <div className="text-sm text-slate-400">Stints</div>
        </div>
        <div className="text-center h-20 flex flex-col justify-center">
          <div className="text-2xl font-bold text-yellow-400">{strategy.lapsPerStint}</div>
          <div className="text-sm text-slate-400">Laps/Stint</div>
        </div>
        <div className="text-center h-20 flex flex-col justify-center">
          <div className="text-2xl font-bold text-red-400">{strategy.pitStops}</div>
          <div className="text-sm text-slate-400">Pit Stops</div>
        </div>
      </div>

      {/* Total race time display */}
      <div className="mb-6 p-4 bg-slate-700 rounded-lg">
        <div className="text-center">
          <div className="text-3xl font-bold text-white">{formatTime(strategy.totalTime)}</div>
          <div className="text-sm text-slate-400">Total Race Time</div>
        </div>
      </div>

      {/* Simple Fuel Save Analysis section - only show for +1 lap strategy */}
      {simpleFuelSaveInfo && title === "+1 Lap Strategy" && (
        <div className="mb-6 p-4 rounded-lg border border-green-500 bg-green-900/20">
          <h3 className="font-semibold mb-2 text-white">+1 Lap Strategy Analysis</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-400">Required Fuel Save Per Lap:</span>
              <span className="ml-2 text-white">{simpleFuelSaveInfo.requiredFuelSave.toFixed(2)}L</span>
            </div>
            <div>
              <span className="text-slate-400">Max Time Loss Per Lap:</span>
              <span className="ml-2 text-white">{simpleFuelSaveInfo.maxTimeLossPerLap.toFixed(2)}s</span>
            </div>
            <div>
              <span className="text-slate-400">Adjusted Fuel Consumption:</span>
              <span className="ml-2 text-white">{simpleFuelSaveInfo.adjustedFuelConsumption.toFixed(2)}L/lap</span>
            </div>
            <div>
              <span className="text-slate-400">Max Lap Time Target:</span>
              <span className="ml-2 text-white">{formatLapTime(simpleFuelSaveInfo.adjustedLapTime)}</span>
            </div>
            <div>
              <span className="text-slate-400">Lap Advantage:</span>
              <span className={`ml-2 font-semibold ${simpleFuelSaveInfo.lapAdvantage >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {simpleFuelSaveInfo.lapAdvantage >= 0 ? '+' : ''}{simpleFuelSaveInfo.lapAdvantage} laps
              </span>
            </div>
            <div>
              <span className="text-slate-400">Time Advantage at Finish:</span>
              <span className={`ml-2 font-semibold ${simpleFuelSaveInfo.timeAdvantageAtFinish >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {simpleFuelSaveInfo.timeAdvantageAtFinish >= 0 ? '+' : ''}{formatTime(simpleFuelSaveInfo.timeAdvantageAtFinish)}
              </span>
            </div>
            <div className="md:col-span-2">
              <div className="p-3 bg-slate-800 rounded-lg">
                <div className="text-white font-medium">Strategy:</div>
                <div className="text-slate-300 text-sm mt-1">
                  Add 1 extra lap to each stint to save fuel and reduce pit stops
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fuel save analysis section - only show for fuel save strategy */}
      {strategy.fuelSave && title === "Fuel Save Strategy" && (
        <div className="mb-6 p-4 rounded-lg border border-blue-500 bg-blue-900/20">
          <h3 className="font-semibold mb-2 text-white">Fuel Save Analysis</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-400">Required Fuel Save Per Stint:</span>
              <span className="ml-2 text-white">{strategy.fuelSave.requiredFuelSave.toFixed(1)}L</span>
            </div>
            <div>
              <span className="text-slate-400">Max Time Loss Per Lap:</span>
              <span className="ml-2 text-white">{strategy.fuelSave.maxTimeLossPerLap.toFixed(2)}s</span>
            </div>
            <div>
              <span className="text-slate-400">Max Lap Time Target:</span>
              <span className="ml-2 text-white">{formatLapTime(strategy.fuelSave.adjustedLapTime)}</span>
            </div>
            <div>
              <span className="text-slate-400">Lap Advantage:</span>
              <span className={`ml-2 font-semibold ${strategy.fuelSave.lapAdvantage >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {strategy.fuelSave.lapAdvantage >= 0 ? '+' : ''}{strategy.fuelSave.lapAdvantage} laps
              </span>
            </div>
            <div>
              <span className="text-slate-400">Time Advantage at Finish:</span>
              <span className={`ml-2 font-semibold ${strategy.fuelSave.timeAdvantageAtFinish >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {strategy.fuelSave.timeAdvantageAtFinish >= 0 ? '+' : ''}{formatTime(strategy.fuelSave.timeAdvantageAtFinish)}
              </span>
            </div>
            <div>
              <span className="text-slate-400">Splash and dash refuel:</span>
              <span className="ml-2 text-white">{strategy.fuelSave.splashAndDashRefuelAmount.toFixed(1)}L</span>
            </div>
            <div>
              <span className="text-slate-400">Splash and dash refuel time:</span>
              <span className="ml-2 text-white">{formatTime(strategy.fuelSave.splashAndDashRefuelTime)}</span>
            </div>
            {onUserActualLapTimeChange && (
              <div className="md:col-span-2">
                <label className="block text-sm text-slate-400 mb-1">Your Actual Achievable Lap Time:</label>
                <input
                  type="text"
                  value={actualLapTimeInputValue}
                  onChange={(e) => {
                    setActualLapTimeInputValue(e.target.value);
                  }}
                  onBlur={() => {
                    const value = actualLapTimeInputValue.trim();
                    if (value === '') {
                      onUserActualLapTimeChange?.(null);
                    } else {
                      // Parse MM:SS.MSMSMS format (accept both : and . for milliseconds)
                      const timeMatch = value.match(/^(\d{1,2}):(\d{2})[.:](\d{3})$/);
                      if (timeMatch) {
                        const minutes = parseInt(timeMatch[1]);
                        const seconds = parseInt(timeMatch[2]);
                        const milliseconds = parseInt(timeMatch[3]);
                        if (seconds < 60) {
                          const lapTime = minutes * 60 + seconds + milliseconds / 1000;
                          onUserActualLapTimeChange?.(lapTime);
                          setActualLapTimeInputValue(formatLapTime(lapTime)); // Format the input
                        }
                      }
                    }
                  }}
                  placeholder={formatLapTime(strategy.fuelSave.adjustedLapTime)}
                  className="w-full px-3 py-2 bg-slate-700 rounded-lg border border-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white text-sm"
                />
                <div className="text-xs text-slate-500 mt-1">
                  Enter your actual achievable lap time to calculate additional time advantage (MM:SS.MSMSMS or MM:SS:MSMSMS)
                </div>
              </div>
            )}
            {onUserActualFuelConsumptionChange && (
              <div className="md:col-span-2">
                <label className="block text-sm text-slate-400 mb-1">Your Actual Fuel Consumption:</label>
                <input
                  type="text"
                  value={actualFuelConsumptionInputValue}
                  onChange={(e) => {
                    setActualFuelConsumptionInputValue(e.target.value);
                  }}
                  onBlur={() => {
                    const value = actualFuelConsumptionInputValue.trim();
                    if (value === '') {
                      onUserActualFuelConsumptionChange?.(null);
                    } else {
                      const fuelConsumption = parseFloat(value);
                      if (!isNaN(fuelConsumption) && fuelConsumption > 0) {
                        onUserActualFuelConsumptionChange?.(fuelConsumption);
                        setActualFuelConsumptionInputValue(fuelConsumption.toFixed(2));
                      }
                    }
                  }}
                  placeholder={strategy.fuelSave.adjustedFuelConsumption.toFixed(2)}
                  className="w-full px-3 py-2 bg-slate-700 rounded-lg border border-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white text-sm"
                />
                <div className="text-xs text-slate-500 mt-1">
                  Enter your actual fuel consumption per lap when fuel saving (L/lap)
                </div>
              </div>
            )}
            <div className="md:col-span-2">
              <span className="text-slate-400">Strategy:</span>
              <span className="ml-2 font-semibold text-blue-400">
                Save {strategy.fuelSave.requiredFuelSave.toFixed(1)}L per stint and don't exceed {formatLapTime(strategy.fuelSave.adjustedLapTime)} per lap
              </span>
            </div>
          </div>
          
          {/* Adjusted Strategy Summary */}
          {strategy.fuelSave.isWorthwhile && strategy.fuelSave.adjustedStrategy && (
            <div className="mt-4 p-3 bg-slate-800/50 rounded-lg">
              <h4 className="font-semibold mb-2 text-green-400">Adjusted Strategy (Fuel Saving Applied)</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <span className="text-slate-400">Total Laps:</span>
                  <span className="ml-2 text-white">{strategy.fuelSave.adjustedStrategy.totalLaps}</span>
                </div>
                <div>
                  <span className="text-slate-400">Laps/Stint:</span>
                  <span className="ml-2 text-white">{strategy.fuelSave.adjustedStrategy.lapsPerStint}</span>
                </div>
                <div>
                  <span className="text-slate-400">Stints:</span>
                  <span className="ml-2 text-white">{strategy.fuelSave.adjustedStrategy.stints}</span>
                </div>
                <div>
                  <span className="text-slate-400">Pit Stops:</span>
                  <span className="ml-2 text-white">{strategy.fuelSave.adjustedStrategy.pitStops}</span>
                </div>
                <div className="md:col-span-2">
                  <span className="text-slate-400">Total Time:</span>
                  <span className="ml-2 text-white">{formatTime(strategy.fuelSave.adjustedStrategy.totalTime)}</span>
                </div>
                <div className="md:col-span-2">
                  <span className="text-slate-400">Time Difference:</span>
                  <span className={`ml-2 font-semibold ${strategy.fuelSave.adjustedStrategy.totalTime < strategy.totalTime ? 'text-green-400' : 'text-red-400'}`}>
                    {strategy.fuelSave.adjustedStrategy.totalTime < strategy.totalTime ? '-' : '+'}
                    {formatTime(Math.abs(strategy.fuelSave.adjustedStrategy.totalTime - strategy.totalTime))}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stint breakdown section */}
      <div className="mb-4 mt-2">
        <h3 className="font-semibold mb-3 text-white">
          {strategy.fuelSave && strategy.fuelSave.isWorthwhile && title === "Fuel Save Strategy" ? 'Adjusted Strategy Stint Breakdown' : 'Stint Breakdown'}
        </h3>
        <div className="space-y-2">
          {(strategy.fuelSave && strategy.fuelSave.isWorthwhile && title === "Fuel Save Strategy" && strategy.fuelSave.adjustedStrategy 
            ? strategy.fuelSave.adjustedStrategy.stintBreakdown 
            : strategy.stintBreakdown
          ).map((stint, index, stints) => {
            // Calculate previous stint end time for timing calculations
            // Include pit stop time in the previous stint's total time
            const previousStintEndTime = index > 0 ? stints.slice(0, index).reduce((total, s) => {
              return total + s.time + (s.pitStopAfter ? s.pitStopTime : 0);
            }, 0) : 0;
            
            const stintDetails = calculateStintDetails(stint, previousStintEndTime, stints[index + 1]);
            
            // Calculate fuel level at start of this stint
            let fuelAtStartOfStint = fuelCapacity; // Start with full tank for first stint
            if (index > 0) {
              // Calculate fuel level step by step
              let currentFuel = fuelCapacity; // Start with full tank
              
              for (let i = 0; i < index; i++) {
                const s = stints[i];
                // Consume fuel for this stint
                currentFuel -= s.fuelUsed;
                // Refuel the amount needed for the next stint if there was a pit stop
                if (s.pitStopAfter) {
                  const nextStint = stints[i + 1];
                  if (nextStint) {
                    // For fuel save strategy, refuel to full capacity (120L)
                    // For normal strategy, refuel the amount needed for the next stint
                    if (title === "Fuel Save Strategy") {
                      currentFuel = fuelCapacity; // Refuel to full tank
                    } else {
                      // Check if next stint is the final stint (no pit stop after it)
                      const isNextStintFinal = !nextStint.pitStopAfter;
                      const safetyMargin = 0.8;
                      
                      // Calculate target fuel level at start of next stint
                      const targetFuel = isNextStintFinal 
                        ? nextStint.fuelUsed + safetyMargin  // Final stint: need fuel used + safety margin
                        : fuelCapacity;                      // Non-final: refuel to full capacity
                      
                      // Set current fuel to target (this represents refueling to the target level)
                      currentFuel = targetFuel;
                    }
                  }
                }
              }
              fuelAtStartOfStint = currentFuel;
            }
            
            const isExpanded = expandedStints.has(stint.stintNumber);
            
            return (
              <div key={stint.stintNumber} className="bg-slate-700 rounded-lg overflow-hidden">
                {/* Stint header - clickable to expand */}
                <div 
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-600 transition-colors min-h-[64px]"
                  onClick={() => toggleStintExpansion(stint.stintNumber)}
                >
                  {/* Stint information */}
                  <div className="flex items-center space-x-4">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {stint.stintNumber}
                    </div>
                    <div>
                      <div className="text-white font-medium">
                        Stint {stint.stintNumber}
                      </div>
                      <div className="text-sm text-slate-400">
                        {stint.laps} laps • {stint.fuelUsed.toFixed(1)}L fuel
                      </div>
                    </div>
                  </div>
                  
                  {/* Stint timing and expand indicator */}
                  <div className="flex items-center space-x-3">
                    <div className="text-right">
                      <div className="text-white font-medium">
                        {formatTime(stint.time)}{stint.pitStopAfter && (
                          <span className="text-yellow-400 ml-2">(+{formatTime(stint.pitStopTime)} pit)</span>
                        )}
                      </div>
                    </div>
                    <div className={`text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                      ▼
                    </div>
                  </div>
                </div>
                
                {/* Expanded stint details */}
                {isExpanded && (
                  <div className="px-3 pb-3 bg-slate-800/50">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-slate-400">Start Time:</span>
                        <div className="text-white font-mono">{formatTime(stintDetails.startTime)}</div>
                      </div>
                      <div>
                        <span className="text-slate-400">End Time:</span>
                        <div className="text-white font-mono">{formatTime(stintDetails.endTime)}</div>
                      </div>
                      <div>
                        <span className="text-slate-400">Pit Stop End:</span>
                        <div className="text-white font-mono">{formatTime(stintDetails.pitStopEndTime)}</div>
                      </div>
                      <div>
                        <span className="text-slate-400">Avg Lap Time:</span>
                        <div className="text-white font-mono">{formatLapTime(stintDetails.avgLapTime)}</div>
                      </div>
                      <div>
                        <span className="text-slate-400">Fuel per Lap:</span>
                        <div className="text-white">{stintDetails.fuelPerLap.toFixed(2)}L</div>
                      </div>
                      <div>
                        <span className="text-slate-400">Fuel Efficiency:</span>
                        <div className="text-white">{(stintDetails.avgLapTime / stintDetails.fuelPerLap).toFixed(1)}s/L</div>
                      </div>
                      <div>
                        <span className="text-slate-400">Refuel Amount:</span>
                        <div className="text-white">{stintDetails.refuelAmount.toFixed(1)}L</div>
                      </div>
                      <div>
                        <span className="text-slate-400">Lap Range:</span>
                        <div className="text-white">
                          {(index === 0 ? 1 : stints.slice(0, index).reduce((total, s) => total + s.laps, 1))} - {(index === 0 ? stint.laps : stints.slice(0, index).reduce((total, s) => total + s.laps, 1) + stint.laps - 1)}
                        </div>
                      </div>
                    </div>
                    
                    {/* Lap-by-lap breakdown within this stint */}
                    <div className="mt-3">
                      <div className="text-slate-400 text-sm mb-2">Lap Details:</div>
                      <div className="space-y-1">
                        {Array.from({ length: stint.laps }, (_, i) => {
                          // Calculate lap number based on stint progression, not time
                          const lapNumber = (index === 0 ? 1 : 
                            stints.slice(0, index).reduce((total, s) => total + s.laps, 1)) + i;
                          
                          const lapStartTime = stintDetails.startTime + (i * stintDetails.avgLapTime);
                          const lapEndTime = lapStartTime + stintDetails.avgLapTime;
                          
                          // Calculate fuel levels properly
                          // Always use the actual fuel consumption per lap from the stint
                          const fuelPerLapForCalculation = stintDetails.actualFuelPerLap;
                          
                          // Start with the fuel level at the beginning of this stint
                          const fuelAtStart = Math.max(0, fuelAtStartOfStint - (i * fuelPerLapForCalculation));
                          // For fuel save strategy, every stint should end with 1L fuel
                          // For normal strategy, every stint should end with 0L fuel (except last stint)
                          
                          // Calculate fuel consumption for this lap
                          // Always consume exactly the target fuel per lap - no artificial adjustments
                          const fuelConsumedThisLap = fuelPerLapForCalculation;
                          const fuelAtEnd = fuelAtStart - fuelConsumedThisLap;
                          
                          const isLastLap = i === stint.laps - 1;
                          const hasPitStop = isLastLap && stint.pitStopAfter;
                          const isFinalLap = lapNumber === strategy.totalLaps;
                          
                          return (
                            <div key={i} className={`flex justify-between text-xs bg-slate-700/50 p-2 rounded ${isFinalLap ? 'border-2 border-green-500' : ''}`}>
                              <span className="text-slate-300">
                                Lap {lapNumber}
                                {isFinalLap && <span className="text-green-400 ml-1">(FINAL)</span>}
                              </span>
                              <span className="text-slate-300">
                                {formatTime(lapStartTime)} - {formatTime(lapEndTime)}
                                {hasPitStop && (
                                  <span className="text-yellow-400 ml-1">
                                    (+{formatTime(stint.pitStopTime)} pit)
                                  </span>
                                )}
                              </span>
                              <span className="text-slate-300">{formatLapTime(stintDetails.avgLapTime)}</span>
                              <span className="text-slate-300">{fuelAtStart.toFixed(2)}L → {fuelAtEnd.toFixed(2)}L</span>
                            </div>
                          );
                        })}
                        {/* Show pit stop details if applicable */}
                        {stint.pitStopAfter && (
                          <div className="text-xs bg-yellow-900/30 p-2 rounded border border-yellow-500/30">
                            <div className="text-yellow-400 font-medium">Pit Stop Details:</div>
                            <div className="text-slate-300">
                              Pit in: {formatTime(pitStopDetails.pitInTime)} | 
                              Pit out: {formatTime(pitStopDetails.pitOutTime)} | 
                              Duration: {formatTime(stint.pitStopTime)} | 
                              Refuel: {stintDetails.refuelAmount.toFixed(1)}L
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
      )}
    </div>
  );
          })}
        </div>
      </div>
    </>}
    </div>
  );
};
